/**
 * AudioSystem — procedural Web Audio API sounds, no audio files required.
 *
 * Lifecycle:
 *   1. Construct early (before any user gesture).
 *   2. Call init() inside a user-gesture handler (click / keydown) to create
 *      the AudioContext and satisfy browser autoplay policy.
 *   3. Call startMusic() whenever you want the background loop to begin.
 */
export class AudioSystem {
  private ctx:      AudioContext | null = null;
  private master:   GainNode    | null = null;
  private sfxBus:   GainNode    | null = null;
  private musicBus: GainNode    | null = null;

  private musicPlaying   = false;
  private musicTimeout:  ReturnType<typeof setTimeout> | null = null;
  private lastShootTime  = -Infinity; // throttle rapid-fire audio

  // ── Bootstrap ─────────────────────────────────────────────────────────────

  init(): void {
    if (this.ctx) { this.ctx.resume(); return; }

    this.ctx      = new AudioContext();
    this.master   = this.ctx.createGain();
    this.sfxBus   = this.ctx.createGain();
    this.musicBus = this.ctx.createGain();

    this.master.gain.value   = 0.8;
    this.sfxBus.gain.value   = 1.0;
    this.musicBus.gain.value = 0.25;

    this.sfxBus.connect(this.master);
    this.musicBus.connect(this.master);
    this.master.connect(this.ctx.destination);

    // Restore mute preference from localStorage
    try {
      if (localStorage.getItem('ironclash_muted') === '1') {
        this.master.gain.value = 0;
      }
    } catch {}
  }

  // ── Low-level helpers ─────────────────────────────────────────────────────

  /** Create a white-noise AudioBufferSourceNode of the given duration. */
  private makeNoise(duration: number): AudioBufferSourceNode {
    const ctx  = this.ctx!;
    const len  = Math.ceil(ctx.sampleRate * (duration + 0.05));
    const buf  = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src  = ctx.createBufferSource();
    src.buffer = buf;
    return src;
  }

  /**
   * Schedule a single oscillator that decays in both frequency and amplitude.
   * @param freqEnd  Target frequency at end of envelope; set equal to freqStart for no pitch change.
   */
  private playOsc(
    bus: GainNode,
    time: number,
    freqStart: number,
    freqEnd: number,
    dur: number,
    vol: number,
    type: OscillatorType = 'sawtooth',
  ): void {
    const ctx  = this.ctx!;
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freqStart, time);
    if (freqEnd !== freqStart) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(freqEnd, 1), time + dur);
    }
    gain.gain.setValueAtTime(vol, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + dur);

    osc.connect(gain);
    gain.connect(bus);
    osc.start(time);
    osc.stop(time + dur + 0.02);
  }

  /**
   * Schedule a noise burst through a biquad filter.
   * @param filterFreqEnd  Optional sweep target for filter frequency.
   */
  private playNoise(
    bus: GainNode,
    time: number,
    filterType: BiquadFilterType,
    filterFreq: number,
    dur: number,
    vol: number,
    filterFreqEnd?: number,
  ): void {
    const ctx    = this.ctx!;
    const noise  = this.makeNoise(dur);
    const filter = ctx.createBiquadFilter();
    const gain   = ctx.createGain();

    filter.type = filterType;
    filter.frequency.setValueAtTime(filterFreq, time);
    if (filterFreqEnd !== undefined) {
      filter.frequency.exponentialRampToValueAtTime(Math.max(filterFreqEnd, 1), time + dur);
    }
    gain.gain.setValueAtTime(vol, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + dur);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(bus);
    noise.start(time);
    noise.stop(time + dur + 0.02);
  }

  // ── SFX ───────────────────────────────────────────────────────────────────

  /**
   * Weapon fire sound — different character per tier.
   * Throttled to one sound per 50 ms so auto-fire doesn't flood the audio graph.
   */
  shoot(weaponTier = 0): void {
    if (!this.ctx || !this.sfxBus) return;
    const now = this.ctx.currentTime;
    if (now - this.lastShootTime < 0.05) return;
    this.lastShootTime = now;

    type Cfg = { f: number; fEnd: number; noise: number; dur: number; type: OscillatorType };
    const configs: Cfg[] = [
      { f: 270,  fEnd: 60,  noise: 0.35, dur: 0.12, type: 'sawtooth' }, // 0 single
      { f: 340,  fEnd: 80,  noise: 0.30, dur: 0.10, type: 'sawtooth' }, // 1 double
      { f: 200,  fEnd: 50,  noise: 0.55, dur: 0.18, type: 'sawtooth' }, // 2 shotgun
      { f: 1800, fEnd: 300, noise: 0.08, dur: 0.07, type: 'sine'     }, // 3 sniper crack
      { f: 100,  fEnd: 25,  noise: 0.50, dur: 0.24, type: 'sawtooth' }, // 4 mortar thump
      { f: 1200, fEnd: 600, noise: 0.04, dur: 0.06, type: 'sine'     }, // 5 laser pew
    ];
    const c = configs[weaponTier] ?? configs[0];

    this.playOsc  (this.sfxBus, now, c.f, c.fEnd, c.dur, 0.40, c.type);
    this.playNoise(this.sfxBus, now, 'bandpass', c.f * 3, c.dur, c.noise);
  }

  /** Metallic impact — play when the local player receives damage. */
  hit(): void {
    if (!this.ctx || !this.sfxBus) return;
    const now  = this.ctx.currentTime;
    const freq = 500 + Math.random() * 600;
    this.playOsc  (this.sfxBus, now, freq, freq * 0.25, 0.14, 0.28, 'triangle');
    this.playNoise(this.sfxBus, now, 'bandpass', freq * 2, 0.10, 0.15);
  }

  /** Low rumbling explosion — use for barrel/crate/tank destruction. */
  explosion(scale = 1.0): void {
    if (!this.ctx || !this.sfxBus) return;
    const now = this.ctx.currentTime;
    const dur = Math.min(1.2, 0.55 * scale);
    this.playOsc  (this.sfxBus, now, 90, 20, dur, Math.min(0.9, 0.55 * scale), 'sawtooth');
    this.playNoise(this.sfxBus, now, 'lowpass', 2500, dur, Math.min(1.0, 0.7 * scale), 200);
  }

  /** Ascending crystal chime — for reward/pickup events. */
  crystalPickup(): void {
    if (!this.ctx || !this.sfxBus) return;
    const now = this.ctx.currentTime;
    [523, 659, 784, 1047].forEach((f, i) => {
      this.playOsc(this.sfxBus!, now + i * 0.065, f, f, 0.22, 0.18, 'sine');
    });
  }

  /** Short ascending fanfare — for level-up events. */
  levelUp(): void {
    if (!this.ctx || !this.sfxBus) return;
    const now = this.ctx.currentTime;
    [392, 494, 587, 784].forEach((f, i) => {
      this.playOsc(this.sfxBus!, now + i * 0.10, f, f, 0.38, 0.25, 'triangle');
    });
  }

  /**
   * Full tank-kill sound — larger explosion for self, standard for remote.
   * When isLocal, appends a dramatic pitch-descending tone.
   */
  machineDestroyed(isLocal = false): void {
    this.explosion(isLocal ? 2.0 : 1.4);
    if (isLocal && this.ctx && this.sfxBus) {
      const now = this.ctx.currentTime;
      this.playOsc(this.sfxBus, now + 0.08, 220, 35, 1.8, 0.32, 'sawtooth');
    }
  }

  // ── Background Music ──────────────────────────────────────────────────────

  startMusic(): void {
    if (!this.ctx || !this.musicBus || this.musicPlaying) return;
    this.musicPlaying = true;
    this.scheduleLoop(this.ctx.currentTime + 0.15);
  }

  stopMusic(): void {
    this.musicPlaying = false;
    if (this.musicTimeout) { clearTimeout(this.musicTimeout); this.musicTimeout = null; }
  }

  private scheduleLoop(t0: number): void {
    if (!this.musicPlaying || !this.ctx || !this.musicBus) return;

    const beat = 60 / 118;  // ≈ 0.508 s  (118 BPM)
    const loop = beat * 16; // 4 bars     (≈ 8.13 s)

    this.scheduleDrums    (t0, beat);
    this.scheduleBass     (t0, beat);
    this.scheduleArpeggio (t0, beat);

    // Re-schedule the next loop 150 ms before this one ends to avoid gaps.
    this.musicTimeout = setTimeout(
      () => this.scheduleLoop(t0 + loop),
      (loop - 0.15) * 1000,
    );
  }

  // ── Drum helpers ──────────────────────────────────────────────────────────

  private kick(t: number): void {
    const b = this.musicBus!;
    this.playOsc  (b, t, 80, 20, 0.35, 0.65, 'sine');
    this.playNoise(b, t, 'lowpass', 200, 0.12, 0.12);
  }

  private snare(t: number): void {
    const b = this.musicBus!;
    this.playOsc  (b, t, 200, 80, 0.18, 0.35, 'sine');
    this.playNoise(b, t, 'bandpass', 1500, 0.18, 0.38);
  }

  private hihat(t: number, open = false): void {
    this.playNoise(this.musicBus!, t, 'highpass', 7000, open ? 0.12 : 0.04, 0.22);
  }

  private scheduleDrums(t0: number, beat: number): void {
    for (let bar = 0; bar < 4; bar++) {
      const b = t0 + bar * beat * 4;

      // Kick on beats 1 & 3, snare on 2 & 4
      this.kick (b);
      this.kick (b + beat * 2);
      this.snare(b + beat);
      this.snare(b + beat * 3);

      // 8th-note hi-hats; open on the "& of 3"
      for (let n = 0; n < 8; n++) {
        this.hihat(b + n * beat * 0.5, n === 5);
      }
    }
  }

  // ── Melodic helpers ───────────────────────────────────────────────────────

  private scheduleBass(t0: number, beat: number): void {
    const b = this.musicBus!;
    // A minor pentatonic bass riff, 4 bars
    const A2=110, E2=82.4, D2=73.4, G2=98.0, C2=65.4;
    type Note = [number, number, number]; // [beatOffset, freq, durBeats]
    const bars: Note[][] = [
      [[0,A2,0.9],[1,A2,0.4],[1.5,E2,0.4],[2,D2,0.9],[3,G2,0.9]],
      [[0,A2,0.9],[1,A2,0.4],[1.5,D2,0.4],[2,E2,0.9],[3,D2,0.9]],
      [[0,A2,0.9],[1,A2,0.4],[1.5,E2,0.4],[2,D2,0.9],[3,G2,0.9]],
      [[0,A2,1.4],[2,C2,0.4],[2.5,D2,0.4],[3,E2,0.9]],
    ];
    bars.forEach((notes, barIdx) => {
      const barStart = t0 + barIdx * beat * 4;
      notes.forEach(([off, freq, dur]) => {
        this.playOsc(b, barStart + off * beat, freq, freq, dur * beat, 0.45, 'sawtooth');
      });
    });
  }

  private scheduleArpeggio(t0: number, beat: number): void {
    const b = this.musicBus!;
    // A minor chord arpeggios across 4 bars
    const [A4,C5,E5,G5,A5] = [440, 523, 659, 784, 880];
    type Pair = [number, number]; // [beatOffset, freq]
    const pattern: Pair[] = [
      [0,A4],[0.5,C5],[1,E5],[1.5,A5],
      [2,G5],[2.5,E5],[3,C5],[3.5,A4],
      [4,E5],[4.5,G5],[5,A5],[5.5,G5],
      [6,E5],[6.5,C5],[7,A4],[7.5,E5],
      [8,A4],[8.5,C5],[9,E5],[9.5,A5],
      [10,G5],[10.5,E5],[11,C5],[11.5,A4],
      [12,A5],[12.5,G5],[13,E5],[13.5,C5],
      [14,A4],[14.5,C5],[15,E5],[15.5,G5],
    ];
    pattern.forEach(([off, freq]) => {
      this.playOsc(b, t0 + off * beat, freq, freq, beat * 0.42, 0.07, 'triangle');
    });
  }

  // ── Match End Jingles ────────────────────────────────────────────────────

  /** Triumphant ascending fanfare — play when the local team wins. */
  victory(): void {
    if (!this.ctx || !this.sfxBus) return;
    const now = this.ctx.currentTime;
    const bus = this.sfxBus;
    // C major ascending power chord: C4 E4 G4 C5 — big and bright
    const notes = [262, 330, 392, 523, 659];
    notes.forEach((f, i) => {
      this.playOsc(bus, now + i * 0.12, f, f, 0.6, 0.3, 'triangle');
      this.playOsc(bus, now + i * 0.12, f * 1.005, f * 1.005, 0.55, 0.15, 'sine'); // chorus
    });
    // Final sustained chord
    [523, 659, 784].forEach((f) => {
      this.playOsc(bus, now + 0.7, f, f, 1.2, 0.2, 'triangle');
    });
  }

  /** Descending minor tones — play when the local team loses. */
  defeat(): void {
    if (!this.ctx || !this.sfxBus) return;
    const now = this.ctx.currentTime;
    const bus = this.sfxBus;
    // A minor descending: A4 E4 C4 A3
    const notes = [440, 330, 262, 220];
    notes.forEach((f, i) => {
      this.playOsc(bus, now + i * 0.2, f, f * 0.98, 0.5, 0.25, 'sawtooth');
    });
    // Low rumble finish
    this.playOsc(bus, now + 0.9, 110, 55, 1.5, 0.2, 'sawtooth');
  }

  /** Ambiguous suspended chord — play on a draw. */
  draw(): void {
    if (!this.ctx || !this.sfxBus) return;
    const now = this.ctx.currentTime;
    const bus = this.sfxBus;
    // Suspended chord: D4 G4 A4 D5
    [294, 392, 440, 588].forEach((f, i) => {
      this.playOsc(bus, now + i * 0.15, f, f, 0.8, 0.22, 'triangle');
    });
  }

  // ── Volume controls ───────────────────────────────────────────────────────

  get muted(): boolean {
    return this.master?.gain.value === 0;
  }

  /** Toggle mute on the master bus; returns the new muted state. */
  toggleMute(): boolean {
    if (!this.master) return true;
    const wasMuted = this.master.gain.value === 0;
    this.master.gain.value = wasMuted ? 0.8 : 0;
    const nowMuted = !wasMuted;
    try { localStorage.setItem('ironclash_muted', nowMuted ? '1' : '0'); } catch {}
    return nowMuted;
  }

  setMasterVolume(v: number): void {
    if (this.master) this.master.gain.value = Math.max(0, Math.min(1, v));
  }

  setMusicVolume(v: number): void {
    if (this.musicBus) this.musicBus.gain.value = Math.max(0, Math.min(1, v));
  }

  setSfxVolume(v: number): void {
    if (this.sfxBus) this.sfxBus.gain.value = Math.max(0, Math.min(1, v));
  }
}
